import { useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { t } from './sprache'

/**
 * Die zwei kleinen Fenster, die das Programm selbst stellt: **eine Frage** und
 * **eine Eingabe** (R394).
 *
 * ---
 *
 * **Warum es sie gibt.** Bis zum 2026-09-12 benutzten fünf Stellen die
 * Browserdialoge `window.prompt` und `window.confirm`. In der Hülle, in der
 * CoKo läuft, gibt es beide nicht:
 *
 * - `prompt()` **wirft** (*„prompt() is not supported"*) — der Aufrufer bricht
 *   mitten im Klick ab, und auf dem Bildschirm geschieht nichts.
 * - `confirm()` gibt **stillschweigend `false`** zurück — die Frage wird nie
 *   gestellt, und der Zweig dahinter läuft nie.
 *
 * Betroffen waren *Neuer Ordner*, *Umbenennen*, *Ordner löschen*, *In
 * Folgeperiode auflösen* und *Kachel zurücksetzen*. Der Anwender klickte, und
 * es passierte nichts — ohne Meldung, ohne Fehler, ohne Anhaltspunkt.
 *
 * **Die Lehre ist nicht „die Hülle ist schuld".** Ein Browserdialog ist auch
 * dort, wo es ihn gibt, das falsche Werkzeug: Er lässt sich nicht beschriften
 * wie das übrige Programm, trägt kein `:focus-visible`, hört auf keine
 * Sprachwahl und blockiert den ganzen Strang. Was das Programm fragt, fragt es
 * ab jetzt selbst.
 *
 * **Beide Fenster folgen den Dialogen, die es schon gibt** (`Loeschfrage`,
 * `Einfuegewahl`): derselbe Schatten, dieselbe Knopfreihe, Esc schließt,
 * Eingabetaste bestätigt, und der Fokus steht beim Öffnen im Fenster (R143).
 */

/**
 * Esc schließt, und beim Öffnen steht der Fokus im Fenster (R143).
 *
 * **Der Verweis wird übergeben, nicht sein Inhalt.** Beim ersten Zeichnen ist
 * `ref.current` noch `null`; wer den Inhalt übergäbe, riefe `focus()` auf
 * nichts — das Fenster ginge auf und der Fokus bliebe dahinter stehen.
 */
export function useFensterverhalten(
  aufSchliessen: () => void,
  zuerst: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    zuerst.current?.focus()

    function beiTaste(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        aufSchliessen()
      }
    }

    window.addEventListener('keydown', beiTaste)
    return () => window.removeEventListener('keydown', beiTaste)
  }, [aufSchliessen, zuerst])
}

/**
 * Eine Ja-Nein-Frage vor einem Schritt, der sich nicht von selbst zurücknimmt.
 *
 * **Die gefährliche Antwort trägt die Warnfarbe und steht vorn**, der Abbruch
 * daneben als Link — dieselbe Anordnung wie in der Löschabfrage des Belegs.
 * Wer schnell klickt, soll nicht versehentlich den schwereren Weg treffen;
 * deshalb liegt der Fokus beim Öffnen auf *Abbrechen*.
 */
export function Nachfrage({
  titel,
  frage,
  hinweis,
  bestaetigen,
  gefahr = false,
  aufJa,
  aufSchliessen,
}: {
  titel: string
  frage: string
  /** Was sonst noch dazugehört — Folgen, Bedingungen, Auflagen. */
  hinweis?: string
  /** Die Beschriftung der bestätigenden Taste — sie **benennt die Tat** (R144). */
  bestaetigen: string
  gefahr?: boolean
  aufJa: () => void
  aufSchliessen: () => void
}) {
  const abbruch = useRef<HTMLButtonElement>(null)
  useFensterverhalten(aufSchliessen, abbruch)

  return (
    <div className="nachweisschatten" onClick={aufSchliessen}>
      <div className="fenster" role="dialog" aria-label={titel} onClick={(e) => e.stopPropagation()}>
        <header>
          <h2>{titel}</h2>
        </header>

        <p className={gefahr ? 'probe abweichung' : 'probe'}>{frage}</p>
        {hinweis && <p className="erlaeuterung">{hinweis}</p>}

        <div className="aktionsreihe">
          <button type="button" className={gefahr ? 'warnung' : undefined} onClick={aufJa}>
            {bestaetigen}
          </button>
          <button type="button" ref={abbruch} onClick={aufSchliessen}>
            {t('beleg.abbrechen')}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * **Was nicht geht, sagt ein Fenster** — mit dem Grund und, wo es einen gibt,
 * dem Weg. Eine Taste, kein Abbruch daneben: Es gibt nichts abzubrechen, und
 * ein zweiter Knopf, der dasselbe tut, beantwortet keine Frage.
 */
export function Hinweisfenster({
  titel,
  meldung,
  weg,
  aufSchliessen,
}: {
  titel: string
  meldung: string
  weg?: string
  aufSchliessen: () => void
}) {
  const verstanden = useRef<HTMLButtonElement>(null)
  useFensterverhalten(aufSchliessen, verstanden)

  return (
    <div className="nachweisschatten" onClick={aufSchliessen}>
      <div className="fenster" role="alertdialog" aria-label={titel} onClick={(e) => e.stopPropagation()}>
        <header>
          <h2>{titel}</h2>
        </header>

        <p className="probe abweichung">{meldung}</p>
        {weg && <p className="erlaeuterung">{weg}</p>}

        <div className="aktionsreihe">
          <button type="button" ref={verstanden} onClick={aufSchliessen}>
            {t('ordner.fehler.verstanden')}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Eine Zeile Text erfragen — ein Ordnername, ein neuer Name.
 *
 * **Ein leerer Name wird nicht angenommen**, und die Taste sagt es, indem sie
 * gesperrt bleibt: Ein Ordner ohne Namen wäre im Baum nicht wiederzufinden.
 */
export function Texteingabe({
  titel,
  beschriftung,
  anfangswert = '',
  platzhalter,
  bestaetigen,
  aufEingabe,
  aufSchliessen,
}: {
  titel: string
  beschriftung: string
  anfangswert?: string
  platzhalter?: string
  bestaetigen: string
  aufEingabe: (wert: string) => void
  aufSchliessen: () => void
}) {
  const [wert, setWert] = useState(anfangswert)
  const feld = useRef<HTMLInputElement>(null)
  useFensterverhalten(aufSchliessen, feld)

  const gueltig = wert.trim() !== ''

  function sende() {
    if (gueltig) {
      aufEingabe(wert.trim())
    }
  }

  return (
    <div className="nachweisschatten" onClick={aufSchliessen}>
      <div className="fenster" role="dialog" aria-label={titel} onClick={(e) => e.stopPropagation()}>
        <header>
          <h2>{titel}</h2>
        </header>

        <div className="feld breit">
          <label htmlFor="texteingabefeld">{beschriftung}</label>
          <input
            id="texteingabefeld"
            ref={feld}
            type="text"
            value={wert}
            placeholder={platzhalter}
            autoComplete="off"
            onChange={(e) => setWert(e.target.value)}
            /*
              **Die Eingabetaste bestätigt.** Ein Fenster mit genau einem Feld
              und genau einer Tat darf nicht verlangen, dass man zur Maus
              greift — das war der einzige Vorteil, den `prompt()` hatte, und
              er geht hier nicht verloren.
            */
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                sende()
              }
            }}
          />
        </div>

        <div className="aktionsreihe">
          <button type="button" disabled={!gueltig} onClick={sende}>
            {bestaetigen}
          </button>
          <button type="button" onClick={aufSchliessen}>
            {t('beleg.abbrechen')}
          </button>
        </div>
      </div>
    </div>
  )
}
